import UserContext from '@/context/user-context'
import { FlashMessageContext } from '@/context/flash-message'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ILoggedInUser } from 'types'
import { SOCIAL_AUTH_POPUP_FEATURES, buildSocialAuthPopupHref, isSocialAuthPopupMessage } from './social-popup'
import type { SocialAuthProvider } from './social-popup'

type SocialAuthState =
    | {
          status: 'idle'
      }
    | {
          provider: SocialAuthProvider
          status: 'closed' | 'pending'
      }

type Options = {
    mode: 'modal' | 'page'
    onSuccess?: (user: ILoggedInUser) => void | Promise<void>
}

export const useSocialAuthPopup = ({ mode, onSuccess }: Options) => {
    const { showMessage } = useContext(FlashMessageContext)
    const { refreshUser } = useContext(UserContext)
    const popupWindow = useRef<Window | null>(null)
    const popupPoll = useRef<number | null>(null)
    const completionInFlight = useRef(false)
    const [socialAuthState, setSocialAuthState] = useState<SocialAuthState>({ status: 'idle' })

    const clearPopupPoll = useCallback(() => {
        if (popupPoll.current !== null) {
            window.clearInterval(popupPoll.current)
            popupPoll.current = null
        }
    }, [])

    const completeSocialAuth = useCallback(async () => {
        if (completionInFlight.current) {
            return
        }

        completionInFlight.current = true
        try {
            const authenticatedUser = await refreshUser?.()
            if (!authenticatedUser) {
                setSocialAuthState((current) =>
                    current.status === 'idle' ? current : { provider: current.provider, status: 'closed' }
                )
                showMessage({
                    message: 'We could not confirm your login. Please try again.',
                    type: 'error',
                })
                return
            }

            clearPopupPoll()
            popupWindow.current?.close()
            popupWindow.current = null
            setSocialAuthState({ status: 'idle' })
            await onSuccess?.(authenticatedUser)
        } finally {
            completionInFlight.current = false
        }
    }, [clearPopupPoll, onSuccess, refreshUser, showMessage])

    const openSocialAuthPopup = useCallback(
        (provider: SocialAuthProvider, href: string) => {
            if (mode !== 'modal') {
                return
            }

            clearPopupPoll()
            const popup = window.open(
                buildSocialAuthPopupHref(href),
                `giphy-${provider}-auth`,
                SOCIAL_AUTH_POPUP_FEATURES
            )

            if (!popup) {
                setSocialAuthState({ status: 'idle' })
                showMessage({
                    message: 'We could not open the login window. Allow popups and try again.',
                    type: 'error',
                })
                return
            }

            popupWindow.current = popup
            setSocialAuthState({ provider, status: 'pending' })
            popup.focus()
            popupPoll.current = window.setInterval(() => {
                if (!popup.closed || completionInFlight.current) {
                    return
                }

                clearPopupPoll()
                setSocialAuthState((current) =>
                    current.status === 'pending' ? { provider: current.provider, status: 'closed' } : current
                )
            }, 500)
        },
        [clearPopupPoll, mode, showMessage]
    )

    useEffect(() => {
        if (mode !== 'modal') {
            return undefined
        }

        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin || !isSocialAuthPopupMessage(event.data)) {
                return
            }

            completeSocialAuth()
        }

        window.addEventListener('message', onMessage)
        return () => {
            window.removeEventListener('message', onMessage)
        }
    }, [completeSocialAuth, mode])

    useEffect(() => clearPopupPoll, [clearPopupPoll])

    return {
        completeSocialAuth,
        openSocialAuthPopup,
        socialAuthState,
    }
}
